/**
 * Guest Upload Events Store
 *
 * Manages guest upload events state using Valtio.
 */

import { proxy } from "valtio";

// =============================================================================
// Types
// =============================================================================

export interface GuestEvent {
  id: string;
  name: string;
  code: string;
  createdAt: number;
  expiresAt: number;
  maxUploads: number;
  uploadCount: number;
  isActive: boolean;
  contributorCount: number;
}

export interface GuestEventDetails extends GuestEvent {
  hostUserId: string;
  contributors: GuestContributor[];
}

export interface GuestContributor {
  name?: string;
  relation?: string;
  uploadedAt: number;
  fileKeys: string[];
}

export interface GuestUpload {
  key: string;
  size: number;
  uploaded: string;
  contributorName?: string;
  contributorRelation?: string;
}

export interface CreateEventRequest {
  name: string;
  expiresInDays?: number;
  maxUploads?: number;
}

export interface CreateEventResponse {
  id: string;
  name: string;
  code: string;
  uploadUrl: string;
  qrCodeUrl: string;
  expiresAt: number;
  expiresInDays: number;
  maxUploads: number;
}

interface EventsStore {
  events: GuestEvent[];
  loading: boolean;
  error: string | null;
  selectedEvent: GuestEventDetails | null;
  selectedEventUploads: GuestUpload[];
  uploadsLoading: boolean;
  createLoading: boolean;
  newEventResponse: CreateEventResponse | null;
}

// =============================================================================
// Store
// =============================================================================

export const eventsStore = proxy<EventsStore>({
  events: [],
  loading: false,
  error: null,
  selectedEvent: null,
  selectedEventUploads: [],
  uploadsLoading: false,
  createLoading: false,
  newEventResponse: null,
});

// =============================================================================
// API Helpers
// =============================================================================

function getUploadWorkerUrl(): string {
  // In production, this would be the deployed worker URL
  // In development, use local worker
  return import.meta.env.VITE_UPLOAD_WORKER_URL || "http://localhost:8787";
}

async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const userId = localStorage.getItem("userId") || "demo-user";

  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": userId,
      ...options.headers,
    },
  });
}

// =============================================================================
// Actions
// =============================================================================

/**
 * Fetch all events for the current user
 */
export async function fetchEvents(): Promise<void> {
  eventsStore.loading = true;
  eventsStore.error = null;

  try {
    const response = await fetchWithAuth(`${getUploadWorkerUrl()}/events/list`);

    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText}`);
    }

    const data = await response.json();
    eventsStore.events = data.events || [];
  } catch (error) {
    eventsStore.error =
      error instanceof Error ? error.message : "Failed to fetch events";
    console.error("Failed to fetch events:", error);
  } finally {
    eventsStore.loading = false;
  }
}

/**
 * Create a new guest upload event
 */
export async function createEvent(
  request: CreateEventRequest,
): Promise<CreateEventResponse | null> {
  eventsStore.createLoading = true;
  eventsStore.error = null;
  eventsStore.newEventResponse = null;

  try {
    const response = await fetchWithAuth(
      `${getUploadWorkerUrl()}/events/create`,
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(
        data.error || `Failed to create event: ${response.statusText}`,
      );
    }

    const data: CreateEventResponse = await response.json();
    eventsStore.newEventResponse = data;

    // Refresh events list
    await fetchEvents();

    return data;
  } catch (error) {
    eventsStore.error =
      error instanceof Error ? error.message : "Failed to create event";
    console.error("Failed to create event:", error);
    return null;
  } finally {
    eventsStore.createLoading = false;
  }
}

/**
 * Get event details with uploads
 */
export async function fetchEventUploads(code: string): Promise<void> {
  eventsStore.uploadsLoading = true;
  eventsStore.error = null;

  try {
    const response = await fetchWithAuth(
      `${getUploadWorkerUrl()}/guest/event/${code}/uploads`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch uploads: ${response.statusText}`);
    }

    const data = await response.json();
    eventsStore.selectedEventUploads = data.uploads || [];

    // Update selected event with contributor info
    if (eventsStore.selectedEvent) {
      eventsStore.selectedEvent.contributors = data.contributors || [];
    }
  } catch (error) {
    eventsStore.error =
      error instanceof Error ? error.message : "Failed to fetch uploads";
    console.error("Failed to fetch uploads:", error);
  } finally {
    eventsStore.uploadsLoading = false;
  }
}

/**
 * Deactivate an event (stop accepting uploads)
 */
export async function deactivateEvent(eventId: string): Promise<boolean> {
  eventsStore.error = null;

  try {
    const response = await fetchWithAuth(
      `${getUploadWorkerUrl()}/events/${eventId}/deactivate`,
      {
        method: "POST",
      },
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(
        data.error || `Failed to deactivate event: ${response.statusText}`,
      );
    }

    // Update local state
    const event = eventsStore.events.find((e) => e.id === eventId);
    if (event) {
      event.isActive = false;
    }

    return true;
  } catch (error) {
    eventsStore.error =
      error instanceof Error ? error.message : "Failed to deactivate event";
    console.error("Failed to deactivate event:", error);
    return false;
  }
}

/**
 * Delete an event and all its uploads
 */
export async function deleteEvent(eventId: string): Promise<boolean> {
  eventsStore.error = null;

  try {
    const response = await fetchWithAuth(
      `${getUploadWorkerUrl()}/events/${eventId}`,
      {
        method: "DELETE",
      },
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(
        data.error || `Failed to delete event: ${response.statusText}`,
      );
    }

    // Remove from local state
    eventsStore.events = eventsStore.events.filter((e) => e.id !== eventId);

    return true;
  } catch (error) {
    eventsStore.error =
      error instanceof Error ? error.message : "Failed to delete event";
    console.error("Failed to delete event:", error);
    return false;
  }
}

/**
 * Select an event for viewing details
 */
export function selectEvent(event: GuestEvent | null): void {
  if (event) {
    eventsStore.selectedEvent = {
      ...event,
      hostUserId: "",
      contributors: [],
    };
    eventsStore.selectedEventUploads = [];
  } else {
    eventsStore.selectedEvent = null;
    eventsStore.selectedEventUploads = [];
  }
}

/**
 * Clear the new event response
 */
export function clearNewEventResponse(): void {
  eventsStore.newEventResponse = null;
}

/**
 * Generate QR code URL for an event code
 */
export function getQRCodeUrl(code: string, size: number = 300): string {
  const uploadUrl = `https://app.vizi.video/guest-upload/${code}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(uploadUrl)}`;
}

/**
 * Get the guest upload URL for an event
 */
export function getUploadUrl(code: string): string {
  return `https://app.vizi.video/guest-upload/${code}`;
}

/**
 * Format date for display
 */
export function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Get time remaining until expiration
 */
export function getTimeRemaining(expiresAt: number): string {
  const now = Date.now();
  const diff = expiresAt - now;

  if (diff <= 0) {
    return "Expired";
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days}d ${hours}h remaining`;
  }
  if (hours > 0) {
    return `${hours}h remaining`;
  }

  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${minutes}m remaining`;
}
